import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export const BACK_ID_DOCUMENT_TYPE_ID = 13;
export const BACK_IMAGE_MAX_BYTES = 10485760;

const BACK_BARCODE_WARNING_TITLES: Record<number, string> = {
  1: "We couldn't read your ID barcode",
  2: "The barcode is still unclear",
  3: "Let's try a sharper photo",
};

const BACK_BARCODE_WARNING_ICONS: Record<number, string> = {
  1: "📷",
  2: "🔍",
  3: "📷",
};

const BACK_BARCODE_WARNING_TIP = "Good lighting · Flat surface · Steady camera";

export const BACK_BARCODE_WARNING_MESSAGES: Record<number, string> = {
  1: "Re-upload a clear photo of the back of your ID. Keep the full ID and barcode visible and in focus.",
  2: "Keep the full back of your ID visible and in focus.",
  3: "Capture the full back of your ID. Tap the barcode on your camera screen to focus before taking the photo.",
};

export function isBackIdDocument(documentId: unknown) {
  return Number(documentId) === BACK_ID_DOCUMENT_TYPE_ID;
}

export function positiveDocumentId(value: unknown) {
  const id = Number(value);
  if (!Number.isSafeInteger(id) || id <= 0) {
    return null;
  }
  return id;
}

export function readBackBarcodeWarning(documentId: unknown, records: any) {
  if (!isBackIdDocument(documentId) || !records || typeof records !== "object") {
    return null;
  }
  if (records.barcode_readable === true) {
    return null;
  }

  const stage = Number(records.barcode_warning_stage);
  if (!Number.isInteger(stage) || stage < 1 || stage > 3) {
    return null;
  }

  return {
    stage,
    documentId: positiveDocumentId(records.id),
    message: BACK_BARCODE_WARNING_MESSAGES[stage],
  };
}

export function prefersCameraForBackIdRetake() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(pointer: coarse)").matches;
}

export function BackBarcodeWarningDialog({
  warning,
  onDismiss,
  onRetake,
  onReplacementSelected,
}: {
  warning: { stage: number; documentId: number | null; message: string } | null;
  onDismiss: () => void;
  onRetake: () => void;
  onReplacementSelected: (file: File) => void;
}) {
  const messageRef = React.useRef("");
  const titleRef = React.useRef("");
  const iconRef = React.useRef("");
  const tipRef = React.useRef("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const message = warning ? BACK_BARCODE_WARNING_MESSAGES[warning.stage] : "";
  const title = warning ? BACK_BARCODE_WARNING_TITLES[warning.stage] : "";
  const icon = warning ? BACK_BARCODE_WARNING_ICONS[warning.stage] : "";
  const tip = warning?.stage === 2 ? BACK_BARCODE_WARNING_TIP : "";
  if (message) {
    messageRef.current = message;
    titleRef.current = title;
    iconRef.current = icon;
    tipRef.current = tip;
  }
  const shownIcon = icon || iconRef.current;
  const shownTitle = title || titleRef.current;
  const shownMessage = message || messageRef.current;
  const shownTip = warning ? tip : tipRef.current;

  const retakeImage = () => {
    const input = fileInputRef.current;
    if (input) {
      const useCamera = prefersCameraForBackIdRetake();
      input.accept = useCamera ? "image/*" : "image/*,application/pdf";
      if (useCamera) {
        input.setAttribute("capture", "environment");
      } else {
        input.removeAttribute("capture");
      }
      input.value = "";
      input.click();
    }
    onRetake();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        data-testid="back-barcode-retake-input"
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) {
            onReplacementSelected(file);
          }
        }}
      />
      <Dialog
        open={Boolean(warning && message)}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onDismiss();
        }}
        aria-labelledby="back-barcode-warning-title"
        aria-describedby="back-barcode-warning-description"
        PaperProps={{
          sx: {
            width: "min(440px, calc(100vw - 32px))",
            maxWidth: "calc(100vw - 32px)",
            m: 2,
          },
        }}
      >
        <DialogTitle id="back-barcode-warning-title" sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <span aria-hidden="true">{shownIcon}</span>
          <span style={{ minWidth: 0 }}>{shownTitle}</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="back-barcode-warning-description">
            {shownMessage}
          </DialogContentText>
          {shownTip ? (
            <DialogContentText sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              <span aria-hidden="true">💡</span>
              <span>{shownTip}</span>
            </DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={onDismiss}
            variant="outlined"
            sx={{
              backgroundColor: "#ffffff",
              color: "#333333",
              borderColor: "#cfcfcf",
              textTransform: "none",
              "&:hover": { backgroundColor: "#f7f7f7", borderColor: "#cfcfcf" },
            }}
          >
            Dismiss
          </Button>
          <Button
            onClick={retakeImage}
            variant="contained"
            autoFocus
            sx={{
              backgroundColor: "#2dc3e8",
              color: "#ffffff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#22b0d3" },
            }}
          >
            Re-Upload Image
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
